import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { HiOutlineChartBar, HiOutlineCalendar, HiOutlineTicket, HiOutlineCash, HiOutlineUsers, HiOutlinePlus } from 'react-icons/hi';
import { db, collection, query, where, getDocs, orderBy, limit, deleteDoc, doc, updateDoc, getDoc, serverTimestamp } from '../config/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { deleteEvent as deleteEventFromService } from '../services/eventService';
import { AttendeesModal } from '../components/AttendeesModal';

interface Event {
  id: string;
  title: string;
  date: Date;
  status: 'draft' | 'published' | 'cancelled' | 'pending_review' | 'rejected';
  attendees: number;
  revenue: number;
  location?: string;
  soldTickets?: number;
  totalTickets?: number;
  createdAt: any;
  rejectionReason?: string;
}

type DashboardTab = 'overview' | 'events' | 'bookings' | 'analytics';

const OrganizerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<{id: string, name: string} | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      try {
        console.log("Fetching events for organizer:", user.uid);
        
        // Create a query to fetch events created by this organizer
        const q = query(
          collection(db, 'events'),
          where('organizerId', '==', user.uid),
        );
        
        console.log("Executing Firestore query");
        const querySnapshot = await getDocs(q);
        console.log(`Query returned ${querySnapshot.docs.length} events`);
        
        if (querySnapshot.empty) {
          console.log("No events found");
          setEvents([]);
          setIsLoading(false);
          return;
        }
        
        // Map the results to our Event interface
        const eventsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log(`Processing event ${doc.id}:`, data);
          
          // Handle possible different date field names and formats
          let eventDate = new Date();
          if (data.startDateTime && typeof data.startDateTime === 'object' && 'toDate' in data.startDateTime) {
            eventDate = data.startDateTime.toDate();
          } else if (data.date && typeof data.date === 'object' && 'toDate' in data.date) {
            eventDate = data.date.toDate();
          } else if (data.startDateTime && data.startDateTime.seconds) {
            eventDate = new Date(data.startDateTime.seconds * 1000);
          } else if (data.date && data.date.seconds) {
            eventDate = new Date(data.date.seconds * 1000);
          }
          
          // Calculate revenue and tickets from ticket tiers if available
          let totalRevenue = 0;
          let soldTickets = 0;
          let totalTickets = 0;
          
          if (data.ticketTiers && Array.isArray(data.ticketTiers)) {
            data.ticketTiers.forEach((tier) => {
              const sold = tier.sold || 0;
              const price = tier.price || 0;
              const quantity = tier.quantity || 0;
              
              soldTickets += sold;
              totalTickets += quantity;
              totalRevenue += sold * price;
            });
          }
          
          return {
            id: doc.id,
            title: data.title || 'Untitled Event',
            date: eventDate,
            status: data.status || 'draft',
            attendees: data.attendees || soldTickets || 0,
            revenue: data.revenue || totalRevenue || 0,
            location: data.location || 'No location specified',
            soldTickets: soldTickets,
            totalTickets: totalTickets,
            createdAt: data.createdAt,
            rejectionReason: data.rejectionReason
          } as Event;
        });
        
        console.log("Processed events:", eventsData);
        setEvents(eventsData);
      } catch (error) {
        console.error('Error fetching organizer data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [user]);

  const handleDeleteEvent = async () => {
    if (!deleteEventId) return;
    
    setIsDeleting(true);
    try {
      await deleteEventFromService(deleteEventId);
      setEvents(events.filter(event => event.id !== deleteEventId));
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    } finally {
      setIsDeleting(false);
      setDeleteEventId(null);
    }
  };

  const handleStatusChange = async (eventId: string, newStatus: 'draft' | 'published' | 'cancelled') => {
    try {
      // Prevent organizers from publishing events directly
      if (newStatus === 'published') {
        toast.error('Only admins can publish events. Your event will be published after admin review.');
        return;
      }

      // Get user document to check roles
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      if (!userData?.roles?.includes('organizer')) {
        throw new Error('You do not have permission to update events');
      }

      await updateDoc(doc(db, 'events', eventId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      setEvents(events.map(event => 
        event.id === eventId ? { ...event, status: newStatus } : event
      ));

      if (newStatus === 'draft') {
        toast.success('Event moved to draft');
      } else if (newStatus === 'cancelled') {
        toast.success('Event cancelled');
      }
    } catch (error) {
      console.error('Error updating event status:', error);
      toast.error('Failed to update event status');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    totalEvents: events.length,
    totalRevenue: events.reduce((sum, event) => sum + event.revenue, 0),
    totalTickets: events.reduce((sum, event) => sum + (event.soldTickets || 0), 0),
    upcomingEvents: events.filter((event) => event.date > new Date()).length
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="w-full max-w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 mx-auto">
      <div className="flex flex-col gap-4 sm:gap-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Organizer Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage your events and track performance</p>
          </div>
          <Link to="/create-event">
            <Button className="flex items-center gap-2">
              <HiOutlinePlus className="h-5 w-5" />
              Create Event
            </Button>
          </Link>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <HiOutlineCalendar className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Events</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalEvents}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <HiOutlineCash className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">${stats.totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <HiOutlineTicket className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tickets Sold</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalTickets}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                <HiOutlineUsers className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Upcoming Events</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.upcomingEvents}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8 border-b">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: HiOutlineChartBar },
              { id: 'events', label: 'Events', icon: HiOutlineCalendar },
              { id: 'bookings', label: 'Bookings', icon: HiOutlineTicket },
              { id: 'analytics', label: 'Analytics', icon: HiOutlineCash }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="overflow-x-auto rounded-lg shadow bg-white dark:bg-gray-900">
          {activeTab === 'overview' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Recent Events</h2>
              <div className="space-y-4">
                {events.map((event: Event) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium">{event.title}</h3>
                      <p className="text-sm text-gray-500">
                        {format(event.date, 'PPP')} • {
                          typeof event.location === 'string' 
                            ? event.location 
                            : typeof event.location === 'object' && event.location !== null
                              ? (event.location as any).address || 'No address'
                              : 'No location'
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${event.revenue.toFixed(2)}</p>
                      <p className="text-sm text-gray-500">
                        {event.soldTickets} / {event.totalTickets} tickets
                      </p>
                    </div>
                  </motion.div>
                ))}
                
                {events.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No events found. Create your first event to get started!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Your Events</h2>
                <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                  <HiOutlinePlus className="h-5 w-5 mr-2" />
                  Create Event
                </button>
              </div>
              <div className="mt-8">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tickets Sold</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.title}</TableCell>
                        <TableCell>{format(event.date, 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeColor(event.status)}>
                            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                          </Badge>
                          {event.status === 'pending_review' && (
                            <span className="ml-2 text-xs text-yellow-600">(Awaiting admin approval)</span>
                          )}
                          {event.status === 'rejected' && event.rejectionReason && (
                            <span className="ml-2 text-xs text-red-600">(Rejected: {event.rejectionReason})</span>
                          )}
                        </TableCell>
                        <TableCell>{event.soldTickets || 0}/{event.totalTickets || 0}</TableCell>
                        <TableCell>${event.revenue.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/edit-event/${event.id}`)}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setDeleteEventId(event.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              title="View Attendees"
                              onClick={() => setSelectedEvent({id: event.id, name: event.title})}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Recent Bookings</h2>
              {/* Bookings list will be implemented here */}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Event Analytics</h2>
              {/* Analytics charts will be implemented here */}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteEventId} onOpenChange={() => setDeleteEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the event
              and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AttendeesModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        eventId={selectedEvent?.id || null}
        eventName={selectedEvent?.name || null}
      />
    </div>
  );
};

export default OrganizerDashboard; 